import { normalizeNetworkQuality } from "@/utils/networkQuality";
import { uploadDiagnosticsRegistry } from "../diagnostics/UploadDiagnosticsRegistry";
import { UPLOAD_EVENT } from "../diagnostics/UploadEvents";
import { getAnalytics } from "../analytics";

/**
 * NetworkTelemetry is a global singleton that watches browser connection state
 * and pushes events into (a) every active UploadDiagnostics and (b) analytics.
 *
 * We reuse the pre-existing `normalizeNetworkQuality` utility rather than
 * duplicate the effectiveType/downlink bucketing rules — one source of truth.
 *
 * Dedupe rule: the connection-change listener fires on any parameter mutation
 * (downlink micro-updates included). We only fan out events when the coarse
 * bucket (online, effectiveType, normalizedQuality) actually changes, so a
 * flaky wifi reading doesn't spam the timeline with 200 identical entries.
 */
let started = false;
let lastSnapshot = null;

/**
 * @returns {import('../diagnostics/UploadEvents').NetworkSnapshot}
 */
export const getNetworkSnapshot = () => {
  const connection = safeConnection();
  const raw = {
    online: safeOnline(),
    effectiveType: connection?.effectiveType || null,
    downlink: typeof connection?.downlink === "number" ? connection.downlink : null,
    rtt: typeof connection?.rtt === "number" ? connection.rtt : null,
    saveData: typeof connection?.saveData === "boolean" ? connection.saveData : null,
  };
  const normalized = normalizeNetworkQuality(raw);
  return {
    ...normalized,
    timestamp: Date.now(),
  };
};

export const startNetworkTelemetry = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  lastSnapshot = getNetworkSnapshot();

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  const connection = safeConnection();
  if (connection && typeof connection.addEventListener === "function") {
    connection.addEventListener("change", handleConnectionChange);
  }
};

const handleOnline = () => {
  const snapshot = getNetworkSnapshot();
  fanOut(UPLOAD_EVENT.NETWORK_ONLINE, snapshot);
  lastSnapshot = snapshot;
};

const handleOffline = () => {
  const snapshot = getNetworkSnapshot();
  fanOut(UPLOAD_EVENT.NETWORK_OFFLINE, snapshot);
  lastSnapshot = snapshot;
};

const handleConnectionChange = () => {
  const next = getNetworkSnapshot();
  if (!hasMeaningfulChange(lastSnapshot, next)) {
    return;
  }
  fanOut(UPLOAD_EVENT.CONNECTION_CHANGED, next);
  lastSnapshot = next;
};

const hasMeaningfulChange = (prev, next) => {
  if (!prev) return true;
  return (
    prev.online !== next.online ||
    prev.effectiveType !== next.effectiveType ||
    prev.normalizedQuality !== next.normalizedQuality
  );
};

const fanOut = (event, snapshot) => {
  // Analytics: global event with no uploadId. Diagnostics: replicate into every
  // in-flight upload so its timeline shows the network transition inline with
  // its own events.
  const analytics = getAnalytics();
  analytics.track(event, snapshot);
  for (const diag of uploadDiagnosticsRegistry.all()) {
    diag.record(event, snapshot, { skipAnalytics: true });
  }
};

const safeConnection = () => {
  if (typeof navigator === "undefined") return null;
  return (
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null
  );
};

const safeOnline = () => {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine !== false;
};

// Test-only reset.
export const __resetNetworkTelemetryForTests = () => {
  if (typeof window !== "undefined") {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
    const connection = safeConnection();
    if (connection && typeof connection.removeEventListener === "function") {
      connection.removeEventListener("change", handleConnectionChange);
    }
  }
  started = false;
  lastSnapshot = null;
};
